import {
  Accordion,
  AccordionItemProps,
  Icon,
  useAccordionItemContext,
} from '@chakra-ui/react';
import { FC, useId } from 'react';
import { MdChevronRight, MdExpandMore } from 'react-icons/md';
import { MAccordionItem, MAccordionPanel, MFlex, MText } from '../Monetize';

interface RevenueContractAccordionProps extends Omit<
  AccordionItemProps,
  'value'
> {
  value?: string;
  title: string;
  hideExpandIcon?: boolean;
  rightContent?: React.ReactNode;
  hidePanel?: boolean;
  children?: React.ReactNode;
}

interface RevenueContractAccordionBodyProps {
  title: string;
  hideExpandIcon?: boolean;
  rightContent?: React.ReactNode;
  hidePanel?: boolean;
  children?: React.ReactNode;
}

// v3 dropped v2's render-prop children (`{({isExpanded}) => ...}`) on
// AccordionItem - the isExpanded-dependent content moves into its own
// child component reading `useAccordionItemContext()`.
const RevenueContractAccordionBody = ({
  title,
  hideExpandIcon,
  rightContent,
  hidePanel,
  children,
}: RevenueContractAccordionBodyProps) => {
  const { expanded: isExpanded } = useAccordionItemContext();

  return (
    <>
      <h2>
        <Accordion.ItemTrigger px={2} minH="unset">
          <MFlex alignItems="center" alignContent="spread" w="full" py={2}>
            <MFlex alignItems="center">
              {!hideExpandIcon && (
                <MFlex alignItems="center">
                  {isExpanded ? (
                    <Icon boxSize="1.5rem" asChild>
                      <MdExpandMore />
                    </Icon>
                  ) : (
                    <Icon boxSize="1.5rem" asChild>
                      <MdChevronRight />
                    </Icon>
                  )}
                </MFlex>
              )}
            </MFlex>
            <MFlex textAlign="left" alignItems="center" py={1} flexGrow={1}>
              <MText fontWeight="500" fontSize="lg" ml={2}>
                {title}
              </MText>
            </MFlex>
            {rightContent}
          </MFlex>
        </Accordion.ItemTrigger>
      </h2>
      {!hidePanel && <MAccordionPanel>{children}</MAccordionPanel>}
    </>
  );
};

export const RevenueContractAccordion: FC<RevenueContractAccordionProps> = ({
  title,
  children,
  hideExpandIcon,
  rightContent,
  hidePanel,
  value,
  ...rest
}) => {
  // v3's Accordion.Item requires a `value` (no implicit index-based
  // identity like v2) - this wrapper's one real caller renders a single
  // standalone item and never had one to give, so fall back to a stable
  // generated id rather than requiring it to invent one.
  const generatedValue = useId();

  return (
    <MAccordionItem border="none" {...rest} value={value ?? generatedValue}>
      <RevenueContractAccordionBody
        title={title}
        hideExpandIcon={hideExpandIcon}
        rightContent={rightContent}
        hidePanel={hidePanel}
      >
        {children}
      </RevenueContractAccordionBody>
    </MAccordionItem>
  );
};
